/**
 * Textual prerequisite status (issue #9). Foundry stores a feat's prerequisites as free text and
 * never enforces them; this module reads the common phrasings against the derived sheet and
 * reports each one as met, unmet or unknown. Nothing here blocks a Selection (docs/design.md,
 * "Legality is enforced structurally and warned textually").
 *
 * Patterns read:
 *   - "trained|expert|master|legendary in <skill | Lore | Perception | save | weapon or armour category>",
 *     with lists ("trained in Arcana, Nature, Occultism, or Religion");
 *   - "<Attribute> +N" (Remaster) and "<Attribute> N" (a Legacy score);
 *   - "level N" / "Nth level";
 *   - the name of an Entry the Character holds (a feat, a class or ancestry feature, a heritage);
 *   - "<name> heritage";
 *   - alternatives joined with "or".
 * Everything else (spellcasting, companions, story conditions) is unknown until a later issue can
 * read it.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import type { DerivedSheet } from "../engine/engine";
import { sluggify } from "../engine/slug";
import { ARMOR_CATEGORIES, ATTRIBUTES, ATTRIBUTE_LABELS, PROFICIENCY_RANKS, SAVES, SKILLS, WEAPON_CATEGORIES, type Attribute, type Rank } from "../engine/values";

export type PrerequisiteStatus = "met" | "unmet" | "unknown";

export interface PrerequisiteVerdict {
  text: string;
  status: PrerequisiteStatus;
  /** Why, in words ("expert in Athletics: trained"). */
  reason: string;
}

/** What the evaluator reads: the sheet plus the names of everything the Character holds. */
export interface PrerequisiteContext {
  sheet: DerivedSheet;
  /** Lower-cased names of the Entries held (feats, features, heritage, ancestry, class ...). */
  heldNames: ReadonlySet<string>;
  /** Lower-cased names of every feat and feature in the catalogue, to tell "unmet" from "unknown". */
  knownNames: ReadonlySet<string>;
  /** The heritage's lower-cased name, if chosen. */
  heritage: string | null;
}

const RANK_BY_NAME: Record<string, Rank> = { untrained: 0, trained: 1, expert: 2, master: 3, legendary: 4 };
const RANK_LABEL: Record<Rank, string> = { 0: "untrained", 1: "trained", 2: "expert", 3: "master", 4: "legendary" };
const SKILL_BY_LABEL = new Map(Object.entries(SKILLS).map(([slug, s]) => [s.label.toLowerCase(), slug]));
const ATTRIBUTE_BY_LABEL = new Map(ATTRIBUTES.map((a) => [ATTRIBUTE_LABELS[a].toLowerCase(), a]));
const SAVE_BY_LABEL = new Map(SAVES.map((s) => [s, s]));
const RECALL_KNOWLEDGE = ["arcana", "crafting", "medicine", "nature", "occultism", "religion", "society"];

/** Build the context once per sheet; the verdicts are then cheap per feat. */
export function prerequisiteContext(sheet: DerivedSheet, catalogue: { records: readonly IndexRecord[] }, heritageName: string | null): PrerequisiteContext {
  const heldNames = new Set<string>();
  for (const item of sheet.items) {
    if (item.inMemory || item.sessionId) continue;
    heldNames.add(item.name.toLowerCase());
    // "Assurance (Athletics)" also satisfies "Assurance".
    const bare = item.name.replace(/\s*\([^)]*\)\s*$/, "").toLowerCase();
    if (bare) heldNames.add(bare);
  }
  const knownNames = new Set<string>();
  for (const r of catalogue.records) {
    if (r.t === "feat" || r.t === "heritage" || r.t === "class" || r.t === "ancestry" || r.t === "background") knownNames.add(r.n.toLowerCase());
  }
  return { sheet, heldNames, knownNames, heritage: heritageName?.toLowerCase() ?? null };
}

/** The status of every prerequisite string, in order. */
export function evaluatePrerequisites(strings: readonly string[], ctx: PrerequisiteContext): PrerequisiteVerdict[] {
  return strings.map((text) => evaluatePrerequisite(text, ctx));
}

/** Overall status: unmet if any is unmet, else unknown if any is unknown, else met. */
export function overallStatus(verdicts: readonly PrerequisiteVerdict[]): PrerequisiteStatus | "none" {
  if (verdicts.length === 0) return "none";
  if (verdicts.some((v) => v.status === "unmet")) return "unmet";
  if (verdicts.some((v) => v.status === "unknown")) return "unknown";
  return "met";
}

export function evaluatePrerequisite(text: string, ctx: PrerequisiteContext): PrerequisiteVerdict {
  const cleaned = text.trim().replace(/[.;]+$/, "");
  const single = evaluateClause(cleaned, ctx);
  if (single) return { text, ...single };
  // Alternatives: "trained in Diplomacy or Intimidation", "Fang Sharpener or razortooth goblin heritage".
  const parts = splitAlternatives(cleaned);
  if (parts.length > 1) {
    const verdicts = parts.map((p) => evaluateClause(p, ctx));
    if (verdicts.some((v) => v?.status === "met")) return { text, status: "met", reason: verdicts.find((v) => v?.status === "met")!.reason };
    if (verdicts.every((v) => v?.status === "unmet")) return { text, status: "unmet", reason: verdicts.map((v) => v!.reason).join("; ") };
    return { text, status: "unknown", reason: "not every alternative can be read" };
  }
  return { text, status: "unknown", reason: "cannot be read from the sheet" };
}

/** Split "a, b, or c" / "a or b" into clauses, carrying a rank prefix into the later ones. */
function splitAlternatives(text: string): string[] {
  if (!/\bor\b/i.test(text)) return [text];
  const raw = text.split(/\s*,\s*(?:or\s+)?|\s+or\s+/i).map((s) => s.trim()).filter(Boolean);
  if (raw.length < 2) return [text];
  const m = /^((?:trained|expert|master|legendary)(?: proficiency)? in )(.*)$/i.exec(raw[0]!);
  if (!m) return raw;
  return raw.map((part, i) => (i === 0 || /^(trained|expert|master|legendary)/i.test(part) ? part : `${m[1]}${part}`));
}

function evaluateClause(clause: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const lower = clause.toLowerCase().trim();
  if (!lower) return null;
  return rankClause(lower, ctx) ?? attributeClause(lower, ctx) ?? levelClause(lower, ctx) ?? heritageClause(lower, ctx) ?? nameClause(lower, ctx);
}

/** "expert in Athletics", "trained in Warfare Lore", "master in Perception", "expert in Fortitude saves", "trained in martial weapons". */
function rankClause(lower: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const m = /^(trained|expert|master|legendary)(?: proficiency)? in (?:the )?(.+)$/.exec(lower);
  if (!m) return null;
  const wanted = RANK_BY_NAME[m[1]!]!;
  const subject = m[2]!.replace(/^(?:a |an |any )/, "").trim();
  // Lists inside one clause ("Arcana, Nature, Occultism, or Religion"): any of them.
  const subjects = subject.split(/\s*,\s*(?:or\s+|and\s+)?|\s+or\s+/).map((s) => s.trim()).filter(Boolean);
  const verdicts = subjects.map((s) => rankOf(s, ctx));
  if (verdicts.some((v) => v === null)) return { status: "unknown", reason: `cannot read the proficiency "${subject}"` };
  const conjunction = /\band\b/.test(subject) && !/\bor\b/.test(subject);
  const results = verdicts.map((v) => v as { label: string; rank: Rank });
  const passing = results.filter((r) => r.rank >= wanted);
  const met = conjunction ? passing.length === results.length : passing.length > 0;
  const reason = results.map((r) => `${r.label}: ${RANK_LABEL[r.rank]}`).join(", ");
  return { status: met ? "met" : "unmet", reason: `${m[1]} in ${subject} (${reason})` };
}

/** The rank the sheet holds for a named skill, Lore, Perception, save, or weapon/armour category; null when unreadable. */
function rankOf(subject: string, ctx: PrerequisiteContext): { label: string; rank: Rank } | null {
  const sheet = ctx.sheet;
  const skillSlug = SKILL_BY_LABEL.get(subject);
  if (skillSlug) return { label: SKILLS[skillSlug as keyof typeof SKILLS].label, rank: sheet.skills[skillSlug]?.rank ?? 0 };
  if (/\blore$/.test(subject)) {
    const slug = sluggify(subject);
    const lore = sheet.skills[slug];
    return { label: subject, rank: lore?.rank ?? 0 };
  }
  if (subject === "perception") return { label: "Perception", rank: sheet.perception.rank ?? 0 };
  const save = /^(fortitude|reflex|will)(?: saves?| saving throws?)?$/.exec(subject)?.[1];
  if (save && SAVE_BY_LABEL.has(save as (typeof SAVES)[number])) return { label: subject, rank: sheet.saves[save as (typeof SAVES)[number]].rank ?? 0 };
  if (subject === "recall knowledge skill" || subject === "recall knowledge skills") {
    const best = Math.max(...RECALL_KNOWLEDGE.map((s) => sheet.skills[s]?.rank ?? 0)) as Rank;
    return { label: "a Recall Knowledge skill", rank: best };
  }
  const weapon = /^(unarmed|simple|martial|advanced)(?: weapons?| attacks?)?$/.exec(subject)?.[1];
  if (weapon && (WEAPON_CATEGORIES as readonly string[]).includes(weapon)) return { label: `${weapon} weapons`, rank: sheet.proficiencies.attacks[weapon]?.rank ?? 0 };
  const armor = /^(unarmored|light|medium|heavy)(?: armou?r| defense)?$/.exec(subject)?.[1];
  if (armor && (ARMOR_CATEGORIES as readonly string[]).includes(armor)) return { label: `${armor} armor`, rank: sheet.proficiencies.defenses[armor]?.rank ?? 0 };
  if (subject === "all skills" || subject === "every skill") return null;
  return null;
}

/** "Strength +2", "Charisma +3"; Legacy "Strength 14". */
function attributeClause(lower: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const m = /^(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+(\+?)(\d+)$/.exec(lower);
  if (!m) return null;
  const attribute = ATTRIBUTE_BY_LABEL.get(m[1]!) as Attribute;
  const n = Number(m[3]);
  const wanted = m[2] === "+" || n <= 7 ? n : Math.floor((n - 10) / 2);
  const have = ctx.sheet.abilities[attribute].value;
  return { status: have >= wanted ? "met" : "unmet", reason: `${ATTRIBUTE_LABELS[attribute]} +${have}` };
}

/** "level 5", "5th level", "at least 5th level". */
function levelClause(lower: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const m = /^(?:at least )?(?:level (\d+)|(\d+)(?:st|nd|rd|th)[- ]level)$/.exec(lower);
  if (!m) return null;
  const wanted = Number(m[1] ?? m[2]);
  return { status: ctx.sheet.level >= wanted ? "met" : "unmet", reason: `Level ${ctx.sheet.level}` };
}

/** "razortooth goblin heritage", "kijimuna gnome heritage". */
function heritageClause(lower: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const m = /^(.+?) heritage$/.exec(lower);
  if (!m) return null;
  const wanted = m[1]!;
  if (ctx.heritage === null) return { status: "unmet", reason: "no heritage chosen" };
  const met = ctx.heritage === wanted || ctx.heritage.startsWith(`${wanted} `) || ctx.heldNames.has(wanted);
  return { status: met ? "met" : "unmet", reason: `heritage: ${ctx.heritage}` };
}

/** The name of an Entry: met when held, unmet when the catalogue knows it, unknown otherwise. */
function nameClause(lower: string, ctx: PrerequisiteContext): Omit<PrerequisiteVerdict, "text"> | null {
  const bare = lower.replace(/\s*\([^)]*\)\s*$/, "");
  if (ctx.heldNames.has(lower) || ctx.heldNames.has(bare)) return { status: "met", reason: "held" };
  if (ctx.knownNames.has(lower) || ctx.knownNames.has(bare)) return { status: "unmet", reason: "not held" };
  return null;
}

export { PROFICIENCY_RANKS };
